import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ComissaoService } from 'app/shared/services/edduca-imob/comissao/comissao.service';

export interface RealizarPagamentoModalData {
  idComissao: number;
  nomeParceiro: string;
  emailParceiro: string;
  chavePix: string;
  valorComissao: number;
  nomeImobiliaria: string;
}

@Component({
  selector: 'app-realizar-pagamento-modal',
  templateUrl: './realizar-pagamento-modal.component.html',
  styleUrls: ['./realizar-pagamento-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSnackBarModule
  ]
})
export class RealizarPagamentoModalComponent implements OnInit {
  form!: UntypedFormGroup;
  isLoading = false;
  arquivoSelecionado: File | null = null;
  arquivoBase64: string | null = null;

  constructor(
    private fb: FormBuilder,
    private comissaoService: ComissaoService,
    private snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<RealizarPagamentoModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RealizarPagamentoModalData
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      arquivo: [null, Validators.required]
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Validar tipo de arquivo (apenas PDF)
      if (file.type !== 'application/pdf') {
        this.snackBar.open('Apenas arquivos PDF são permitidos', 'Fechar', { duration: 3000 });
        return;
      }

      // Validar tamanho (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        this.snackBar.open('O arquivo deve ter no máximo 5MB', 'Fechar', { duration: 3000 });
        return;
      }

      this.arquivoSelecionado = file;
      this.form.patchValue({ arquivo: file });

      // Converter para Base64
      const reader = new FileReader();
      reader.onload = () => {
        this.arquivoBase64 = (reader.result as string).split(',')[1];
      };
      reader.readAsDataURL(file);
    }
  }

  removerArquivo(): void {
    this.arquivoSelecionado = null;
    this.arquivoBase64 = null;
    this.form.patchValue({ arquivo: null });
  }

  realizarPagamento(): void {
    if (this.form.invalid || !this.arquivoBase64) {
      this.snackBar.open('Por favor, anexe o comprovante de pagamento', 'Fechar', { duration: 3000 });
      return;
    }

    this.isLoading = true;

    const request = {
      idComissao: this.data.idComissao,
      comprovanteBase64: this.arquivoBase64
    };

    this.comissaoService.realizarPagamento(request).subscribe({
      next: () => {
        this.snackBar.open('Pagamento realizado com sucesso!', 'Fechar', { duration: 3000 });
        this.dialogRef.close(true);
      },
      error: (err) => {
        console.error('Erro ao realizar pagamento:', err);
        this.snackBar.open('Erro ao realizar pagamento. Tente novamente.', 'Fechar', { duration: 3000 });
        this.isLoading = false;
      }
    });
  }

  copiarChavePix(): void {
    navigator.clipboard.writeText(this.data.chavePix).then(() => {
      this.snackBar.open('Chave PIX copiada!', 'Fechar', { duration: 2000 });
    });
  }

  fechar(): void {
    this.dialogRef.close(false);
  }
}
